import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { cn } from '@rocco/utils/cn';
import { useHydrated } from 'remix-utils';
import { useDirectoryListImpression } from '~/modules-rocco/directory/use-directory-list-impression';
import { BackToTop } from '~/ui-rocco/components/BackToTop';
import { EDITORIAL_GROUND } from '../config/editorial-ground';
import {
    EDITORIAL_RAIL,
    EditorialDirectoryOpening,
} from './EditorialDirectoryOpening';
import {
    MagicIsland,
    MagicIslandProvider,
} from '~/ui-rocco/pages/detail-shared';
import { EditorialFilterBar } from './EditorialFilterBar';
import { EditorialDirectoryMobileHero } from './EditorialDirectoryMobileHero';
import { EditorialSearchResults } from './EditorialSearchResults';
import type { ArticleSearchResults } from '../config/article-search';
import { DigitalPublicationsRow } from './DigitalPublicationsRow';
import type { DigitalPublicationOption } from '@modules-rocco/subscription/api/get-digital-publication-options.server';
import type { FilterStates } from '@modules-rocco/filters/types/filter-states';
import { useArticlesFilterSort } from '../hook/use-articles-filter-sort';
import type { EditorialCategory } from './EditorialCategorySection';
import { EditorialCategorySection } from './EditorialCategorySection';
import type { RevampCategoryChip } from '../type';
import type { CategoryNavItem } from '../util/derive-category-rail';
import { deriveCategoryRail } from '../util/derive-category-rail';
import { ArticleHeroPinProvider } from '~/modules-rocco/admin/components/ArticleHeroPinContext';

/**
 * The masthead article. Sourced from the directory's featured slides rather
 * than the plain tile list: only that shape carries the article's intro text
 * and its full-bleed banner image, both of which the design needs.
 */
export interface EditorialHeroArticle {
    title: string;
    link: string;
    content?: string;
    categoryLabel?: string;
    /** The author words — author / professional / ArchiPro. */
    label?: string;
    date?: string;
    image?: { src?: string };
    /** Control label, where a curated placement overrides the default. */
    ctaLabel?: string;
}

interface ArticlesDirectoryRevampProps {
    /**
     * The directory's opening statement, set the way the category pages set
     * theirs: what the library is, in a sentence or two.
     */
    statement: string;
    /** The digital publications the row offers, in catalogue order. */
    publications: DigitalPublicationOption[];
    /**
     * One per category the CMS shows in the article nav, in nav order. The
     * tabs and the page's sections are drawn from this one list, so they can
     * never fall out of step with each other.
     */
    categories: EditorialCategory[];
    /**
     * The category rail's state, so this page carries the same bar the
     * category pages do.
     */
    filterStates: FilterStates;
    /** The nav tree the rail is drawn from — the same one the theme pages read. */
    navItems: CategoryNavItem[];
    /**
     * The articles matching the term in the rail's search field, when one has
     * been committed. Set, they take the place of the category sections.
     */
    searchResults?: ArticleSearchResults | null;
    /**
     * Articles staff have pinned to the hero carousel. Only used to label the
     * tile's own menu; the carousel itself is already built from these.
     */
    heroPinnedArticleIDs: number[];
    /**
     * The featured-article slides for the phone masthead. The route supplies
     * them for mobile devices only, alongside the transparent page theme the
     * hero sits under; empty, no hero mounts and the page opens on the
     * statement as before.
     */
    heroArticles?: EditorialHeroArticle[];
}

/**
 * The rail's pills: every category in the nav tree — the same tree the theme
 * pages read, so the row is the same row on every editorial page and a tab
 * changes which pill is on and nothing else. The bar itself puts the
 * directory's own pill at the head.
 */
const toChips = (
    navItems: CategoryNavItem[],
    pathname: string,
): RevampCategoryChip[] => deriveCategoryRail(navItems, pathname).items;

/**
 * Composes the revamped /articles directory landing page.
 *
 * Section rhythm follows the Figma frame (60:1948): a 100px spacer under the
 * masthead and 65px between the Trending Categories block and the grid.
 */
export const ArticlesDirectoryRevamp = ({
    statement,
    publications,
    categories,
    filterStates,
    navItems,
    heroPinnedArticleIDs,
    heroArticles = [],
    searchResults,
}: ArticlesDirectoryRevampProps) => {
    const { pathname } = useLocation();
    const chips = useMemo(
        () => toChips(navItems, pathname),
        [navItems, pathname],
    );
    const { searchFilters } = useArticlesFilterSort(filterStates);

    const hydrated = useHydrated();

    // The articles directory never had a list impression — products, projects
    // and professionals each logged one, so editorial browse was the one
    // library whose "viewed, with these refinements" was unmeasurable. The
    // revamp encodes category in the path rather than the query, so the path
    // tail is the category.
    useDirectoryListImpression({
        event: 'articleListImpression',
        source: 'articles',
        dimensions: {
            category:
                pathname.split('/').filter(Boolean).slice(1).join(' | ') ||
                'all',
        },
    });

    return (
        <ArticleHeroPinProvider pinnedArticleIDs={heroPinnedArticleIDs}>
            <MagicIslandProvider>
                <main
                    // overflow-x-clip contains the carousel's full-bleed breakout. Its
                    // 100vw counts the vertical scrollbar, which the visible viewport
                    // does not, so on any page long enough to scroll it overhangs
                    // by the scrollbar's width and raises a horizontal scrollbar. The
                    // overhang is symmetric, so clipping both sides leaves the media
                    // centred and edge to edge. `clip` rather than `hidden`: it does not
                    // become a scroll container, so sticky descendants still work.
                    // `relative`, so the wordmark's absolute box resolves
                    // against this column and reaches up over the header the
                    // way the category pages' does.
                    // Its own ground: the directory sits on a warm paper
                    // rather than the site's grey, the one library that does.
                    className={cn(
                        'relative flex flex-col overflow-x-clip pb-10 md:pb-[10rem]',
                        EDITORIAL_GROUND,
                    )}
                    data-testid="articles-directory-revamp"
                >
                    {heroArticles.length > 0 ? (
                        <>
                            <EditorialDirectoryMobileHero
                                articles={heroArticles}
                            />
                            {/* The masthead replaces the opening on the
                                phone: the page goes hero → rail. The name
                                stays for the accessibility tree. */}
                            <h1 className="sr-only">Articles</h1>
                        </>
                    ) : (
                        <EditorialDirectoryOpening statement={statement} />
                    )}

                    {/* The publications, as covers alone, between the opening
                    and the tabs (Figma 0:81): the opening already carries
                    their line and the Subscribe. 65px above, the design's
                    small spacer, and twice that below before the rail. */}
                    {/* Below md the covers step after the rail (order-1, with
                        the sections at order-2), so the pills sit directly
                        under the opening the way every category page's do.
                        md+ keeps the Figma order: covers between opening and
                        rail. */}
                    <DigitalPublicationsRow
                        publications={publications}
                        hideIntro
                        className="order-1 mt-10 mb-0 md:order-none md:mt-[4.0625rem] md:mb-[8.125rem]"
                    />

                    {/* The category pages' own rail, not a lookalike: same
                    pills, same refine control, same rule under it. "All
                    Articles" is the active one here, since this is the
                    directory root. */}
                    <EditorialFilterBar
                        items={chips}
                        searchFilters={searchFilters}
                        // Directly under the masthead when it leads the page;
                        // under the opening's own spacer otherwise.
                        className={
                            heroArticles.length > 0 ? 'mt-10' : undefined
                        }
                        {...EDITORIAL_RAIL}
                        // The trail here would read "(All Articles)" — the name of
                        // the page the reader is already on. The rule under it
                        // stays.
                        showSectionHeading={false}
                    />

                    {/* A committed search lists its matches where the
                    sections would be. One section per tab otherwise, in nav
                    order: each shows a turn of the grid and then hands the
                    reader to that category's own page. Both take the phone's
                    flex order (after the covers) and its 40px rhythm. */}
                    {searchResults ? (
                        <EditorialSearchResults
                            results={searchResults}
                            className="order-2 mt-10 md:order-none md:mt-[3rem]"
                        />
                    ) : null}
                    {searchResults
                        ? null
                        : categories.map((category, index) => (
                              <EditorialCategorySection
                                  key={category.link}
                                  category={category}
                                  // The first section opens the page directly under the
                                  // tab bar, which already names it — so it starts on its
                                  // cards rather than on a heading a reader just read.
                                  hideHeading={index === 0}
                                  // Picture, eyebrow and title: the directory is a
                                  // wall of cards, and an opening line under each
                                  // made it a wall of copy.
                                  hideStandfirst
                                  // One palette across pictures from every hand,
                                  // so the grid reads as a magazine spread rather
                                  // than a contact sheet.
                                  graded
                                  className={
                                      index === 0
                                          ? 'order-2 mt-10 md:order-none md:mt-0'
                                          : 'order-2 mt-20 md:order-none md:mt-[6.25rem]'
                                  }
                              />
                          ))}
                </main>

                {/* Fixed chrome, so it sits outside the page flow. Held back until
                hydration: it is driven entirely by client-side carousel state
                and has nothing to render on the server. */}
                {hydrated ? <MagicIsland /> : null}
                {/* The same control the product, project and browse pages carry.
                The page runs long once the grid has paged a few times. */}
                {hydrated ? <BackToTop /> : null}
            </MagicIslandProvider>
        </ArticleHeroPinProvider>
    );
};
